import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import { 
  Check, 
  Leaf,
  Zap,
  Building2,
  ArrowRight,
  Crown,
  Star,
  User,
  GraduationCap,
  Briefcase
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
    if (token) {
      fetchCurrentSubscription();
    }
  }, [token]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentPlan(response.data);
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!token) {
      navigate("/register");
      return;
    }

    setProcessingPlan(planId);
    try {
      const response = await axios.post(
        `${API_URL}/api/subscribe`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.redirect && response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success(response.data.message);
        fetchCurrentSubscription();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la souscription");
    } finally {
      setProcessingPlan(null);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case "personal": return User;
      case "starter": return Leaf;
      case "pro": return Zap;
      case "enterprise": return Building2;
      default: return Leaf;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case "personal": return "bg-blue-500";
      case "starter": return "bg-chart-1";
      case "pro": return "bg-primary";
      case "enterprise": return "bg-chart-5";
      default: return "bg-primary";
    }
  };

  const personalPlans = plans.filter(p => p.type === "personal");
  const businessPlans = plans.filter(p => p.type === "business");

  const renderPricingContent = () => (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
          {t('pricing.title')}
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {t('pricing.subtitle')}
        </p>
      </div>

      {/* Current Plan Badge */}
      {currentPlan && (
        <div className="text-center mb-8">
          <Badge variant="outline" className="px-4 py-2 text-sm">
            <Crown className="w-4 h-4 mr-2" />
            {t('pricing.currentPlan')} : {currentPlan.plan_name}
          </Badge>
        </div>
      )}

      {/* Personal Plan - Highlighted */}
      {personalPlans.length > 0 && (
        <div className="mb-16">
          <div className="text-center mb-8">
            <Badge className="bg-blue-500 text-white px-4 py-1 mb-4">
              <GraduationCap className="w-4 h-4 mr-2" />
              {t('pricing.personal.badge')}
            </Badge>
            <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              {t('pricing.personal.title')}
            </h2>
          </div>
          <div className="max-w-lg mx-auto">
            {personalPlans.map((plan) => {
              const Icon = getPlanIcon(plan.id);
              const isCurrentPlan = currentPlan?.plan_id === plan.id;
              
              return (
                <Card 
                  key={plan.id}
                  className={`border-2 border-blue-500/50 shadow-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5 ${isCurrentPlan ? 'border-blue-500' : ''}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  <CardHeader className="text-center pb-2">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-500 flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                      {plan.name}
                    </CardTitle>
                    <CardDescription>
                      {t('pricing.personal.description')}
                    </CardDescription>
                    <div className="mt-4">
                      <span className="text-5xl font-bold text-blue-600">{plan.price}€</span>
                      <span className="text-muted-foreground text-sm">{t('pricing.perMonth')}</span>
                      <p className="text-sm text-muted-foreground mt-1">{t('pricing.noCommitment')}</p>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className="w-full rounded-full bg-blue-500 hover:bg-blue-600"
                      data-testid={`select-plan-${plan.id}`}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={isCurrentPlan || processingPlan === plan.id}
                    >
                      {processingPlan === plan.id ? t('pricing.loading') : isCurrentPlan ? t('pricing.currentPlan') : t('pricing.personal.cta')}
                      {!isCurrentPlan && processingPlan !== plan.id && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Business Plans */}
      <div>
        <div className="text-center mb-8">
          <Badge className="bg-primary text-primary-foreground px-4 py-1 mb-4">
            <Briefcase className="w-4 h-4 mr-2" />
            {t('pricing.business.badge')}
          </Badge>
          <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            {t('pricing.business.title')}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {businessPlans.map((plan) => {
            const Icon = getPlanIcon(plan.id);
            const isCurrentPlan = currentPlan?.plan_id === plan.id;
            const isPopular = plan.popular;

            return (
              <Card 
                key={plan.id}
                className={`relative card-hover ${isPopular ? 'border-primary border-2 shadow-lg' : ''} ${isCurrentPlan ? 'bg-primary/5' : ''}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-4">
                      <Star className="w-3 h-3 mr-1" />
                      {t('pricing.popular')}
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-2">
                  <div className={`w-14 h-14 mx-auto rounded-2xl ${getPlanColor(plan.id)} flex items-center justify-center mb-4`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                    {plan.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">
                      {`${plan.price}€`}
                    </span>
                    <span className="text-muted-foreground text-sm">{t('pricing.perMonth')}</span>
                  </div>
                </CardHeader>

                <CardContent className="pt-6">
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isPopular ? 'text-primary' : 'text-chart-1'}`} />
                        <span className="text-sm text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full rounded-full ${isPopular ? '' : 'bg-muted text-foreground hover:bg-muted/80'}`}
                    variant={isPopular ? "default" : "outline"}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isCurrentPlan || processingPlan === plan.id}
                    data-testid={`subscribe-btn-${plan.id}`}
                  >
                    {processingPlan === plan.id ? (
                      t('pricing.loading')
                    ) : isCurrentPlan ? (
                      t('pricing.currentPlan')
                    ) : (
                      <>
                        {t('pricing.subscribe')}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* FAQ / Info */}
      <div className="mt-16 text-center">
        <p className="text-muted-foreground text-sm">
          {t('pricing.footer')}<br />
          {t('pricing.customPlan')} <a href="mailto:contact@vfactions.com" className="text-primary hover:underline">{t('pricing.contactUs')}</a>
        </p>
      </div>
    </div>
  );

  // Public view (not logged in)
  if (!token) {
    return (
      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Leaf className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>VF Decide</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost">{t('nav.home')}</Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost">{t('nav.login')}</Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-full px-6">
                  {t('nav.start')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        <main className="pt-28 pb-16 px-6" data-testid="pricing-page-public">
          {renderPricingContent()}
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 bg-card border-t border-border">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Leaf className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-semibold text-foreground">VF Decide</span>
            </div>
            <p className="text-sm text-muted-foreground">
              2026 VF Decide - Plateforme RSE/CSRD
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // Logged in view with sidebar
  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      <main className="flex-1 p-8 ml-64" data-testid="pricing-page-logged">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          renderPricingContent()
        )}
      </main>
    </div>
  );
}
